import { useEffect, useRef, useState, type FormEvent } from 'react';
import PageHeader from '../components/PageHeader';
import StatusPill from '../components/StatusPill';
import { useActiveCompany } from '../state';
import { useCurrentUser } from '../CurrentUserProvider';
import { updateUserProfile } from '../../lib/entities/onboarding';
import type { Company } from '../types';
import { Clock, Cross, Padlock, Shield, Tick } from '../components/AppIcons';
import '../../styles/app-company.css';

/**
 * The Business-tier monthly price for a headcount, or null above 50 where pricing is custom
 * ("Contact us"). Kept in step with the TIERS table below so the pill and the highlighted tier row
 * never disagree.
 */
function tierPrice(headcount: number): number | null {
  if (headcount <= 4) return 50;
  if (headcount <= 9) return 100;
  if (headcount <= 50) return 200;
  return null;
}

/** Show a flag for a moment, then clear it — the optimistic "Saved" toast. */
function useTransientFlag(ms = 2800): [boolean, () => void] {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!on) return;
    const t = setTimeout(() => setOn(false), ms);
    return () => clearTimeout(t);
  }, [on, ms]);
  return [on, () => setOn(true)];
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/** Edit the signed-in user's own name and role. */
function PersonalDetailsCard() {
  const { user, refetch } = useCurrentUser();
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [role, setRole] = useState(user?.role ?? '');
  const [saved, showSaved] = useTransientFlag();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitLock = useRef(false);

  // Seed once the user arrives (the provider loads it async on shell mount).
  useEffect(() => {
    if (!user) return;
    setFirstName(user.firstName ?? '');
    setLastName(user.lastName ?? '');
    setRole(user.role ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const canSave =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    role.trim().length > 0 &&
    !submitting;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitLock.current || !canSave) return;
    submitLock.current = true;
    setSubmitting(true);
    setError(null);
    try {
      await updateUserProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        role: role.trim(),
      });
      // Refetch so the dashboard greeting reflects the new name.
      await refetch();
      showSaved();
    } catch (err) {
      setError(errorText(err, 'Could not save your details. Try again.'));
    } finally {
      setSubmitting(false);
      submitLock.current = false;
    }
  };

  return (
    <section className="acard acard--pad">
      <h2 className="acard__title">Your details</h2>
      <p className="acard__sub">How Reeve addresses you, and the level it pitches things at.</p>

      <form onSubmit={(e) => void onSubmit(e)}>
        <div className="co-form">
          <div className="field">
            <label className="field__label" htmlFor="me-first">
              First name
            </label>
            <input
              id="me-first"
              className="input"
              type="text"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="me-last">
              Last name
            </label>
            <input
              id="me-last"
              className="input"
              type="text"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>

          <div className="field co-form__full">
            <label className="field__label" htmlFor="me-role">
              Your role
            </label>
            <input
              id="me-role"
              className="input"
              type="text"
              autoComplete="organization-title"
              placeholder="e.g. Founder, Office Manager"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <p className="co-ob__error" role="alert">
            {error}
          </p>
        )}

        <div className="co-cardfoot">
          <button type="submit" className="btn btn-primary btn-xs" disabled={!canSave}>
            {submitting ? 'Saving…' : 'Save changes'}
          </button>
          {saved && (
            <span className="co-saved" role="status">
              <Tick size={14} />
              Saved
            </span>
          )}
        </div>
      </form>
    </section>
  );
}

function PrivacyCard() {
  const [expiry, setExpiry] = useState('30');

  return (
    <section className="acard acard--pad">
      <h2 className="acard__title">Privacy &amp; data</h2>
      <p className="acard__sub">
        Your HR questions are sensitive. Here's what happens to them, and what never does.
      </p>

      <div className="co-form">
        <div className="field">
          <label className="field__label" htmlFor="co-expiry">
            Conversations expire after
          </label>
          <select
            id="co-expiry"
            className="select"
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
          >
            <option value="1">1 day</option>
            <option value="2">2 days</option>
            <option value="7">7 days</option>
            <option value="14">14 days</option>
            <option value="30">1 month</option>
            <option value="60">2 months</option>
            <option value="90">3 months</option>
          </select>
          <span className="field__hint">Conversations you save are kept until you delete them.</span>
        </div>
      </div>

      <div className="co-promises">
        <p className="co-promise">
          <Padlock size={17} />
          Your documents and employee records are stored encrypted, at rest and in transit.
        </p>
        <p className="co-promise">
          <Clock size={17} />
          Conversations with the assistant expire by default, so there's usually nothing left to
          disclose if anyone ever asks.
        </p>
        <p className="co-promise">
          <Shield size={17} />
          Your data is never used to train AI models. It's used only to answer your questions and
          keep your requirements current.
        </p>
      </div>
    </section>
  );
}

const TIERS = [
  { band: 'Up to 4 employees', price: '£50/mo', min: 0, max: 4 },
  { band: '5–9 employees', price: '£100/mo', min: 5, max: 9 },
  { band: '10–50 employees', price: '£200/mo', min: 10, max: 50 },
  { band: '50+ employees', price: 'Contact us', min: 51, max: Number.POSITIVE_INFINITY },
];

function PlanCard({ company }: { company: Company }) {
  // The billed number is this company's effective headcount (see mapCompany).
  const headcount = company.employees;
  const price = tierPrice(headcount);

  return (
    <section className="acard acard--pad">
      <div className="co-cardhead">
        <h2 className="acard__title">Plan &amp; billing</h2>
        <StatusPill variant="ok" dot={false}>
          {price === null ? 'Business plan · Contact us' : `Business plan · £${price}/mo`}
        </StatusPill>
      </div>
      <p className="acard__sub">One price for everything, stepped by team size.</p>

      <div className="co-tiers">
        {TIERS.map((tier) => {
          const isCurrent = headcount >= tier.min && headcount <= tier.max;
          return (
            <div key={tier.band} className={`co-tiers__row${isCurrent ? ' is-current' : ''}`}>
              <span className="co-tiers__band">
                {tier.band}
                {isCurrent && (
                  <StatusPill variant="ok" dot={false}>
                    Your tier
                  </StatusPill>
                )}
              </span>
              <span className="co-tiers__price">{tier.price}</span>
            </div>
          );
        })}
      </div>

      <p className="co-billing-note">
        Your plan is priced on your headcount: {headcount} {headcount === 1 ? 'person' : 'people'}{' '}
        right now.
      </p>
      <p className="co-billing-note">
        Expert help is billed on demand: you only pay for a human when you actually use one.
      </p>
    </section>
  );
}

function LeavingCard({ company }: { company: Company }) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <section className="acard acard--pad">
      <h2 className="acard__title">Data &amp; leaving</h2>
      <p className="acard__sub">
        Your data stays yours. Close this company down for good whenever you need to.
      </p>

      <div className="co-cardfoot">
        {/* TODO: restore when data export is implemented */}
        <button type="button" className="co-danger" onClick={() => setConfirmOpen(true)}>
          Delete this company
        </button>
      </div>

      {confirmOpen && (
        <div className="modal-scrim" onClick={() => setConfirmOpen(false)}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Delete ${company.name}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="modal__close"
              aria-label="Close"
              onClick={() => setConfirmOpen(false)}
            >
              <Cross size={18} />
            </button>
            <h3 className="modal__title">Delete {company.name}?</h3>
            <p className="co-modal-copy">
              This removes the company along with its documents, cases and conversations. This can't
              be undone.
            </p>
            <p className="co-modal-note">This is a demo, so nothing will actually be deleted.</p>
            <div className="co-modal-actions">
              <button
                type="button"
                className="btn btn-secondary btn-xs"
                onClick={() => setConfirmOpen(false)}
              >
                Keep the company
              </button>
              <button
                type="button"
                className="btn btn-xs co-btn-danger"
                onClick={() => setConfirmOpen(false)}
              >
                Delete company
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default function SettingsPage() {
  const { company, loading, companyLoading } = useActiveCompany();

  // Plan & leaving hang off the active company; wait for it before reading fields.
  if (!company) {
    const busy = loading || companyLoading;
    return (
      <div className="apage">
        <PageHeader title="Settings" />
        <div className="acard">
          <div className="empty">
            {busy ? 'Loading your settings…' : 'Add a company to manage its settings.'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="apage">
      <PageHeader title="Settings" lead="Your account, privacy and billing." />

      <div className="co-stack">
        <PersonalDetailsCard />
        <PrivacyCard />
        <PlanCard company={company} />
        <LeavingCard company={company} />
      </div>
    </div>
  );
}
